const SEARCH_DEBOUNCE = 250;

const buildSearchUrl = function(baseUrl, query) {
	if (!baseUrl || !query) {
		return '';
	}

	const url = new URL(baseUrl, window.location.origin);
	url.searchParams.set('search', query);

	return url.toString();
};

export const createSearchBehavior = function({elm, state, inputSelector}) {
	let debounceTimer = 0;
	let controller = null;

	const clearRequestState = function() {
		window.clearTimeout(debounceTimer);

		if (controller) {
			controller.abort();
			controller = null;
		}
	};

	const clearResults = function() {
		clearRequestState();
		state.set(s => {
			s.items = [];
			s.isLoading = false;
			s.hasFetched = false;
		});
	};

	const reset = function() {
		clearResults();
		state.set(s => s.query = '');
	};

	const focusInput = function() {
		const input = elm.querySelector(inputSelector);
		if (!input) {
			return;
		}

		input.focus();

		if (input.value) {
			input.setSelectionRange(input.value.length, input.value.length);
		}
	};

	const requestSuggestions = async function(query) {
		clearRequestState();

		const requestController = new AbortController();
		controller = requestController;

		state.set(s => {
			s.isLoading = true;
			s.hasFetched = false;
		});

		try {
			const url = new URL(state.get().suggestUrl, window.location.origin);
			url.searchParams.set('search', query);

			const response = await fetch(url.toString(), {
				signal: requestController.signal,
				headers: {
					'X-Requested-With': 'XMLHttpRequest'
				}
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const result = await response.json();
			const items = Array.isArray(result.items) ? result.items : [];

			if (controller !== requestController) {
				return;
			}

			state.set(s => {
				s.items = items;
				s.isLoading = false;
				s.hasFetched = true;
			});
		} catch (error) {
			if (error?.name === 'AbortError') {
				return;
			}

			console.error(error);

			if (controller !== requestController) {
				return;
			}

			state.set(s => {
				s.items = [];
				s.isLoading = false;
				s.hasFetched = false;
			});
		} finally {
			if (controller === requestController) {
				controller = null;
			}
		}
	};

	const handleInput = function(e) {
		const query = e.target.value;
		const minLength = Number(state.get().minLength) || 2;

		state.set(s => s.query = query);

		if (query.trim().length < minLength) {
			clearResults();

			return;
		}

		window.clearTimeout(debounceTimer);
		debounceTimer = window.setTimeout(() => {
			void requestSuggestions(query.trim());
		}, SEARCH_DEBOUNCE);
	};

	const submit = function(e) {
		e.preventDefault();

		const {resultsUrl} = searchView(state.get());
		if (!resultsUrl) {
			return;
		}

		window.location.assign(resultsUrl);
	};

	return {
		clearResults,
		focusInput,
		handleInput,
		reset,
		submit
	};
};

export const searchView = function(state) {
	const query = typeof state.query === 'string' ? state.query : '';
	const trimmedQuery = query.trim();
	const items = Array.isArray(state.items) ? state.items : [];
	const minLength = Number(state.minLength) || 2;
	const hasMinLength = trimmedQuery.length >= minLength;
	const hasItems = items.length > 0;
	const resultsUrl = buildSearchUrl(state.searchPageUrl, trimmedQuery);
	const showNoResults = hasMinLength && state.hasFetched && !state.isLoading && !hasItems;
	const showAllResultsLink = Boolean(resultsUrl);
	const showDropdown = hasMinLength && (state.isLoading || hasItems || showNoResults || showAllResultsLink);

	return {
		...state,
		items,
		query,
		resultsUrl,
		hasItems,
		showDropdown,
		showNoResults,
		showAllResultsLink
	};
};
